import { useMemo, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { fromJS } from 'immutable';
import { Capabilities } from 'flow/utils/capability-utils';
import {
    createAndShowChatSession,
    inviteChatMembers,
} from 'flow/actions/chat-actions';
import SettingRow from 'flow/components/Setting/SettingRow';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Button from 'flow/components/ui-components/Button';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import SearchInput from 'flow/components/ui-components/SearchInput';
import useAppContext from 'flow/hooks/useAppContext';
import { ChatMember } from 'flow/types/Chats';
import { Contact } from 'flow/types/Contact';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';

import { TypedMap } from 'flow/types/TypedMap';
import { extractName, sortByName } from 'flow/utils';
import {
    filterByAllfileds,
    getExtensionHasCapability,
} from 'flow/utils/extension-utils-TW';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';

interface SelectedMember {
    contact: TypedMap<Contact>;
    chatUserKey: string;
}

interface Props {
    id?: ChatSessionKey;
}

const AddMessageMemberModal = ({ id }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const [searchTerm, setSearchTerm] = useState([]);
    const ref = useRef<HTMLDivElement | null>(null);
    const timeoutId = useRef<NodeJS.Timeout | number>(null);
    const [selectedMembers, setSelectedMembers] = useState<SelectedMember[]>(
        [],
    );

    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );

    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', id]),
    );

    const isNotAlreadyMember = (e: TypedMap<Extension>) =>
        !selectedMembers.some((m) => m.chatUserKey === e.get('chatUserKey')) &&
        !session
            .get('members')
            .valueSeq()
            .some(
                (m: TypedMap<ChatMember>) =>
                    m.get('extensionKey') === e.get('key'),
            );

    const { isSinglePaneMode } = useSinglePaneMode();

    const hasChatCapability = (e: TypedMap<Extension>) =>
        getExtensionHasCapability(e, Capabilities.CHAT);

    const filteredExtensions = useMemo(
        () =>
            extensions
                .filter(isNotAlreadyMember)
                .filter(hasChatCapability)
                .filter((e: TypedMap<Extension>) =>
                    filterByAllfileds(e, searchTerm),
                )
                .sort(sortByName)
                .valueSeq(),
        [extensions, searchTerm.join(' '), selectedMembers],
    );

    const handleInput = (text: string) => {
        clearTimeout(timeoutId.current as number);

        timeoutId.current = setTimeout(() => {
            setSearchTerm(
                text
                    .split(/\s+/)
                    .filter((w) => w !== '')
                    .map((w) => w.toLowerCase()),
            );
        }, 400);

        ref.current.scrollTo({ top: 0 });
    };

    const handleSubmit = () => {
        const membersToAdd = selectedMembers.map((m) => ({
            key: m.chatUserKey,
        }));

        let memberList: any = session.get('members');
        const isNotChatRoom = !session.get('isChatRoom');

        if (isNotChatRoom) {
            const chatMembers: { key: string }[] = [];

            membersToAdd.map((chatUserKey) => chatMembers.push(chatUserKey));
            memberList = memberList.concat(fromJS(chatMembers));

            const chatSession = {
                members: memberList.toJS(),
            };
            dispatch(createAndShowChatSession(chatSession));
        } else {
            dispatch(inviteChatMembers(session.get('key'), membersToAdd));
        }
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('chatsettings.add_member')}
            className={
                isSinglePaneMode ? 'size-full' : 'h-[39.5rem] !w-[600px]'
            }
            confirmButtonText={i18next.t('chatsettings.add')}
            disableSubmit={selectedMembers.length === 0}
            onSubmit={handleSubmit}
        >
            <div className="flex flex-col">
                <div className="flex flex-col gap-y-2">
                    <SearchInput
                        iconKey="search"
                        onChange={(e) => handleInput(e.target?.value)}
                        autoFocus={true}
                    />

                    <div className="flex flex-wrap gap-2">
                        {selectedMembers.map((m) => (
                            <div
                                key={m.chatUserKey}
                                className="flex items-center justify-between gap-x-4 rounded-sm border border-gray-300 ps-1"
                            >
                                <div className="flex items-center gap-x-1">
                                    <ContactAvatar
                                        size="small"
                                        contact={m.contact}
                                        lazyLoad={true}
                                    />
                                    <p className="text-sm">
                                        {m.contact.get('firstName')}
                                    </p>
                                </div>
                                <Button
                                    className="text-gray-400"
                                    iconKey="cancel"
                                    onClick={() =>
                                        setSelectedMembers((prev) =>
                                            prev.filter(
                                                (p) =>
                                                    p.chatUserKey !==
                                                    m.chatUserKey,
                                            ),
                                        )
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div ref={ref}>
                    {filteredExtensions.map((e: TypedMap<Extension>) => {
                        const contact = e.get('contact');

                        return (
                            <div key={e.get('key')}>
                                <SettingRow
                                    key={e.get('key')}
                                    cta={
                                        <Button
                                            iconKey="add"
                                            className="reveal-on-hover-or-focus flex"
                                            onClick={() =>
                                                setSelectedMembers((prev) => [
                                                    ...prev,
                                                    {
                                                        contact,
                                                        chatUserKey:
                                                            e.get(
                                                                'chatUserKey',
                                                            ),
                                                    },
                                                ])
                                            }
                                        />
                                    }
                                >
                                    <ContactAvatar
                                        contact={contact}
                                        lazyLoad={true}
                                    />
                                    <p className="text-gray-800">
                                        {extractName(contact)}
                                    </p>
                                </SettingRow>
                            </div>
                        );
                    })}
                </div>
            </div>
        </BaseModal>
    );
};

export default AddMessageMemberModal;
